import { portalLocale, portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The workspace's funding line — plan-014 unit **D6** (design §27.15.1 `Billing`, `Transactions` (changed); the
 * prototype's banner at the top of `Billing` and the kicker of `Transactions`, `design/WeTalk.dc.html`).
 *
 * Which balance pays for the open workspace and since when, from `GET /v1/billing/funding`; for someone with the
 * organization's finance (or a dedicated balance's delegate) a link to Finance, where the other balances, plans, caps
 * and invoices are; for everyone else a plain sentence that the organization's money is its Owners', Admins' and
 * Finance's and that this page shows the workspace's spend. Never a sibling workspace's name unless the server named it.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Icon } from "@wetalk/ui";
import type { ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import type { WorkspaceFundingView } from "../../api/slice/financial-document.js";
import { GLYPH } from "../../shell/glyph.js";

export const WORKSPACE_FUNDING_KEY = ["workspace-funding"] as const;

/** The open workspace's funding, shared by Billing and Transactions. */
export function useWorkspaceFunding(): { readonly funding: WorkspaceFundingView | undefined; readonly failed: boolean } {
  const gateway = useGateway();
  const query = useQuery({ queryKey: WORKSPACE_FUNDING_KEY, queryFn: () => gateway.readWorkspaceFunding() });
  return { funding: query.data, failed: query.isError };
}

const SINCE = () => new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-GB"), { day: "numeric", month: "long" });

/** "the shared balance since 4 June" / "its own balance, Trastevere balance, since 12 September". */
export function balancePhraseOf(funding: WorkspaceFundingView): string {
  const since = funding.since === null ? "" : portalText("finance", "m0267", { value1: SINCE().format(new Date(funding.since)) });
  if (funding.scope === "account") return portalText("finance", "m0268", { value1: since });
  return portalText("finance", "m0269", { value1: funding.displayName === null ? "" : `, ${funding.displayName},`, value2: since });
}

/** "funds Roma — main and Summer pop-up" — or the count when the names are not the person's to see. */
export function fundsPhraseOf(funding: WorkspaceFundingView): string {
  if (!funding.fundedWorkspaceNamesComplete) {
    const others = funding.fundedWorkspaceCount - 1;
    return others <= 0 ? portalText("finance", "m0270") : portalText("finance", "funding.others", { count: others });
  }
  const names = funding.fundedWorkspace.map((one) => one.workspaceName);
  if (names.length <= 1) return portalText("finance", "m0272", { value1: names[0] ?? portalText("finance", "m0187") });
  return portalText("finance", "m0273", { value1: names.slice(0, -1).join(", "), value2: names[names.length - 1] ?? "" });
}

export function FundingLine(props: { readonly funding: WorkspaceFundingView; readonly workspaceName: string }): ReactNode {
  usePortalTranslation("finance");
  const { funding } = props;
  const mayFinance = funding.financeAccess !== "none";
  const sentence = (
    <span className="bl-funding-text">{portalText("finance", "m0274")}<strong>{props.workspaceName}</strong>{portalText("finance", "m0275")}{balancePhraseOf(funding)}.{" "}
      {mayFinance
        ? portalText("finance", "m0276")
        : portalText("finance", "m0277")}
    </span>
  );
  if (!mayFinance) {
    return (
      <p className="bl-funding" role="note">
        <Icon glyph={GLYPH.bank} size="lg" />
        {sentence}
      </p>
    );
  }
  return (
    <Link to="/finance/funding" className="bl-funding bl-funding-link">
      <Icon glyph={GLYPH.bank} size="lg" />
      {sentence}
      <Icon glyph={GLYPH.arrowRight} size="md" />
    </Link>
  );
}
